"use client";

import Image from "next/image";
import Section from "./section";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";

interface ImageTextSectionProps {
  title: string;
  badge?: string;
  content: React.ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  imagePosition?: 'left' | 'right';
  className?: string;
}

export default function ImageTextSection({
  title,
  badge,
  content,
  imageSrc,
  imageAlt = "Section image",
  imagePosition = 'right',
  className
}: ImageTextSectionProps) {

  const ImageComponent = (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="relative h-[400px] md:h-[500px] lg:h-[600px] group"
    >
      {/* Decorative frame */}
      <div className={cn(
        "absolute -inset-3 bg-linear-to-br from-primary/20 via-secondary/10 to-transparent rounded-2xl -z-10",
        imagePosition === 'left' ? "rotate-2" : "-rotate-2"
      )} />

      <div className="relative h-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border/50">
        {imageSrc ? (
          <>
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-105"
            />
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-linear-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </>
        ) : (
          <div className="h-full bg-linear-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <p className="text-sm">Image Placeholder</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );

  const TextComponent = (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="space-y-6 flex flex-col justify-center h-full"
    >
      {/* Badge */}
      {badge && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            {badge}
          </span>
        </motion.div>
      )}

      {/* Title with decorative line */}
      <div className="space-y-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight"
        >
          {title}
        </motion.h2>
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="h-1 w-20 bg-linear-to-r from-primary to-secondary rounded-full origin-left"
        />
      </div>

      {/* Content with staggered paragraphs */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="space-y-4 text-lg md:text-xl text-muted-foreground leading-relaxed [&>p]:relative [&_strong]:text-foreground [&_strong]:font-semibold"
      >
        {content}
      </motion.div>
    </motion.div>
  );

  return (
    <Section className={cn("py-12 lg:py-20", className)}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {imagePosition === 'left' ? (
          <>
            {ImageComponent}
            {TextComponent}
          </>
        ) : (
          <>
            {TextComponent}
            {ImageComponent}
          </>
        )}
      </div>
    </Section>
  );
}
